import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, EMPTY, finalize, Observable, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { api } from '../common';
import { recordRumError, recordRumEvent } from './monitoring/aws-rum';
import { ClientDiagnosticsService, SKIP_CLIENT_DIAGNOSTICS } from './services/client-diagnostics.service';
import { SessionExpiryService } from './auth/session-expiry.service';
import { environment } from '../environments/environment';

let accessTokenRefreshInFlight$: Observable<string> | undefined;

export const authHttpInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Diagnostics must not initiate or influence session recovery. Otherwise an expired token on the
  // diagnostics upload can recursively trigger refresh/retry events and change the OIDC auth state.
  if (req.context.get(SKIP_CLIENT_DIAGNOSTICS)) {
    return next(req);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const diagnostics = inject(ClientDiagnosticsService);
  const sessionExpiry = inject(SessionExpiryService);
  const tokenRequest = req.url.split('?')[0] === `${environment.cognitoDomain}/oauth2/token`;
  if (tokenRequest) sessionExpiry.startRefresh();
  if (isApiRequest(req) && sessionExpiry.isEndingSession) return EMPTY;

  return next(req).pipe(
    catchError((error) => {
      if (isApiRequest(req) && sessionExpiry.isEndingSession) return EMPTY;
      if (tokenRequest && isTransientRefreshError(error)) {
        sessionExpiry.preserveSessionOnTransientFailure();
      }
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        const apiRequest = isApiRequest(req);
        const path = apiRequest ? diagnostics.toApiPath(req.url) : stripUrlQuery(req.url) || req.url;

        recordRumEvent('auth.api_error', {
          app: 'front',
          method: req.method,
          url: stripUrlQuery(req.url),
          status: error.status,
          message: error.error?.message || error.message,
        });

        if (apiRequest) {
          diagnostics.trackApi(
            'auth.api_error',
            {
              method: req.method,
              path,
              status: error.status,
              message: error.error?.message || error.message,
            },
            'warn',
          );
        }

        if (error.status === 401 && apiRequest) {
          diagnostics.trackApi(
            'auth.refresh.started',
            {
              method: req.method,
              path,
              status: error.status,
            },
            'info',
          );

          return refreshAccessToken(oidcSecurityService, sessionExpiry).pipe(
            catchError((refreshError) => {
              const refreshStatus = extractHttpStatus(refreshError);
              const refreshMessage = truncateForRum(extractErrorMessage(refreshError), 300);

              recordRumError('auth_api_refresh_failed', {
                app: 'front',
                source: 'auth_http_interceptor',
                method: req.method,
                url: stripUrlQuery(req.url),
                status: error.status,
                refreshStatus,
                refreshUrl: stripUrlQuery(extractErrorUrl(refreshError)),
                refreshMessage,
              });

              diagnostics.trackApi(
                'auth.refresh.failed',
                {
                  method: req.method,
                  path,
                  status: refreshStatus,
                  triggerStatus: error.status,
                  refreshUrl: stripUrlQuery(extractErrorUrl(refreshError)),
                  message: refreshMessage,
                },
                'error',
              );

              if (isTransientRefreshError(refreshError) || sessionExpiry.hasTransientRefreshFailure) {
                sessionExpiry.preserveSessionOnTransientFailure();
                return throwError(() => refreshError);
              }
              sessionExpiry.expire();
              return EMPTY;
            }),
            switchMap((accessToken) => {
              if (sessionExpiry.isEndingSession) return EMPTY;
              if (!accessToken) {
                recordRumError('auth_api_refresh_missing_token', {
                  app: 'front',
                  source: 'auth_http_interceptor',
                  method: req.method,
                  url: stripUrlQuery(req.url),
                  status: error.status,
                });

                diagnostics.trackApi(
                  'auth.refresh.missing_token',
                  {
                    method: req.method,
                    path,
                    status: error.status,
                  },
                  'error',
                );

                sessionExpiry.expire();
                return EMPTY;
              }

              recordRumEvent('auth.api_401_retry', {
                app: 'front',
                method: req.method,
                url: stripUrlQuery(req.url),
              });

              diagnostics.trackApi(
                'auth.api_401_retry',
                {
                  method: req.method,
                  path,
                },
                'info',
              );

              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                }),
              ).pipe(
                tap((event) => {
                  if (event instanceof HttpResponse) {
                    diagnostics.trackApi(
                      'auth.api_401_retry.succeeded',
                      {
                        method: req.method,
                        path,
                        status: event.status,
                        requestId: event.headers.get('x-request-id') || undefined,
                      },
                      'info',
                    );
                  }
                }),
                catchError((retryError) => {
                  if (sessionExpiry.isEndingSession) return EMPTY;
                  diagnostics.trackApi(
                    'auth.api_401_retry.failed',
                    {
                      method: req.method,
                      path,
                      status: extractHttpStatus(retryError),
                      requestId:
                        retryError instanceof HttpErrorResponse
                          ? retryError.headers?.get('x-request-id') || undefined
                          : undefined,
                      message: truncateForRum(extractErrorMessage(retryError), 300),
                    },
                    'error',
                  );
                  if (extractHttpStatus(retryError) === 401) {
                    sessionExpiry.expire();
                    return EMPTY;
                  }
                  return throwError(() => retryError);
                }),
              );
            }),
          );
        }
      }
      return throwError(() => error);
    }),
  );
};

function refreshAccessToken(
  oidcSecurityService: OidcSecurityService,
  sessionExpiry: SessionExpiryService,
): Observable<string> {
  if (!accessTokenRefreshInFlight$) {
    sessionExpiry.startRefresh();
    accessTokenRefreshInFlight$ = oidcSecurityService.forceRefreshSession().pipe(
      switchMap(() => oidcSecurityService.getAccessToken().pipe(take(1))),
      finalize(() => {
        accessTokenRefreshInFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  return accessTokenRefreshInFlight$;
}

function isApiRequest(req: HttpRequest<unknown>): boolean {
  return req.url.startsWith(api);
}

function isTransientRefreshError(error: unknown): boolean {
  const status = extractHttpStatus(error);
  if (status === 0 || status === 408 || status === 429 || (status !== undefined && status >= 500)) return true;
  // OIDC wraps HttpErrorResponse in Error during refresh, dropping its structured status.
  return /\b(network|timeout|failed to fetch|unknown error)\b|Http failure response.*:\s*(0|408|429|5\d{2})\b/i.test(
    extractErrorMessage(error),
  );
}

function extractHttpStatus(error: unknown): number | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }

  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }

  return undefined;
}

function extractErrorUrl(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.url ?? undefined;
  }

  if (error && typeof error === 'object' && 'url' in error && typeof error.url === 'string') {
    return error.url;
  }

  const messageUrl = extractErrorMessage(error).match(/https?:\/\/[^\s)]+/);
  return messageUrl?.[0];
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.error?.message || error.message || String(error.status);
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return String(error);
}

function stripUrlQuery(url: string | undefined): string | undefined {
  return url?.split('?')[0];
}

function truncateForRum(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}
